// Kinio plane api：家戶（docs/KINIO-API.md §2）。base = homes[].endpoints.api。

import { kinioFetch } from './auth';
import { planeBase } from './config';
import type { ArmedModeResult, HomeDetail, HomeRecord, HomeThingBinding, KinioHome } from './types';

/** GET {api}/v1/homes */
export async function listHomes(apiEndpoint: string): Promise<HomeRecord[]> {
  const res = await kinioFetch<{ homes?: HomeRecord[] }>(planeBase(apiEndpoint) + '/v1/homes');
  return Array.isArray(res?.homes) ? res.homes : [];
}

/** GET {api}/v1/homes/{home_id} → home + things[] + members[] */
export async function getHome(apiEndpoint: string, homeId: string): Promise<HomeDetail> {
  const res = await kinioFetch<HomeDetail>(`${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}`);
  return { home: res.home, things: Array.isArray(res.things) ? res.things : [], members: Array.isArray(res.members) ? res.members : [], endpoints: res.endpoints };
}

/** 便捷：直接用 identity 回的家戶摘要（帶 endpoints.api）。 */
export function getHomeOf(home: KinioHome): Promise<HomeDetail> {
  return getHome(home.endpoints.api, home.home_id);
}

/**
 * PUT {api}/v1/homes/{home_id}/armed-mode `{"armed_mode":"home|night|away"}`（ADR-011 §4.1 唯一寫入口；一律小寫，大寫 422，platform#78）
 * → 200 `{home_id, armed_mode, things:[{device_ref, pushed_to_device, status}]}`；422 `INVALID_ARGUMENT`（detail.field = armed_mode）、403 無權限、409 `HOME_UNASSIGNED`。
 */
export function putArmedMode(apiEndpoint: string, homeId: string, armedMode: string): Promise<ArmedModeResult> {
  return kinioFetch<ArmedModeResult>(`${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/armed-mode`, {
    method: 'PUT',
    body: { armed_mode: armedMode },
  });
}

/** 設備名稱上限（SPEC #data-1202：去頭尾空白後 1–32 字、不含控制字元）。 */
export const THING_NAME_MAX = 32;

/** 設備名稱 client 端檢查；回 null = 合法。 */
export function validateThingName(name: string): 'required' | 'too_long' | 'control_chars' | null {
  const v = name.trim();
  if (!v) return 'required';
  if (/[\u0000-\u001f\u007f-\u009f]/.test(v)) return 'control_chars';
  return Array.from(v).length > THING_NAME_MAX ? 'too_long' : null;
}

/**
 * PATCH {api}/v1/homes/{home}/things/{device} `{display_name}`（owner / admin）→ 200 Thing 摘要（同 getHome 的 things[] 一筆）。
 * `null` = 清掉、回到設備自己的名字。422 INVALID_NAME；403 不是 owner / admin。
 */
export function renameThing(apiEndpoint: string, homeId: string, device: string, displayName: string | null): Promise<HomeThingBinding> {
  return kinioFetch<HomeThingBinding>(`${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(device)}`, {
    method: 'PATCH',
    body: { display_name: displayName === null ? null : displayName.trim() },
  });
}
